import { Link, NavLink } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { cn } from '../shared/lib/utils'
import type { RootState } from '../shared/store'
import { getSyncStatusMeta } from '../features/sync/services/syncStatusMeta'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../shared/components/ui/sheet'
import { Separator } from '../shared/components/ui/separator'
import { Button } from '../shared/components/ui/button'
import { BrandLogo } from '../shared/components/brand/BrandLogo'
import { getJobDrawerActions, type NavItem } from './navigation'
import { SearchTrigger } from './SearchTrigger'
import type { JobContext } from './DrawerContext'

interface MobileDrawerProps {
  isOpen: boolean
  onClose: () => void
  navLinks: NavItem[]
  // Optional job context - only provided when viewing a job
  jobContext?: JobContext
  /** Opens the sync status dialog (closes the drawer first) */
  onOpenSyncStatus: () => void
  /** Opens the search palette (closes the drawer first); omitted for roles
      with nothing to search */
  onOpenSearch?: () => void
}

/**
 * MobileDrawer component
 * - Side navigation drawer for mobile devices (left side)
 * - Shows app branding, search, navigation links, and job context
 * - Independent scrolling when content exceeds viewport height
 */
export const MobileDrawer = ({
  isOpen,
  onClose,
  navLinks,
  jobContext,
  onOpenSyncStatus,
  onOpenSearch,
}: MobileDrawerProps) => {
  // Read the slice directly — useSyncStatus would register duplicate
  // online/offline listeners (SyncStatusDialog owns that hook)
  const { isOnline, isSyncing, pendingCount, failedCount } = useSelector(
    (state: RootState) => state.sync
  )
  const syncMeta = getSyncStatusMeta({ isOnline, isSyncing, pendingCount, failedCount })

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent
        side="left"
        hideClose
        className="flex w-[312px] flex-col rounded-r-3xl bg-sidebar p-0 text-sidebar-foreground"
      >
        {/* Header - Fixed */}
        <SheetHeader className="px-6 py-4 border-b text-left">
          <SheetTitle className="text-left">
            <Link to="/dashboard" className="inline-flex" onClick={onClose}>
              <BrandLogo className="h-8 w-auto" />
            </Link>
          </SheetTitle>
          <SheetDescription className="sr-only">
            Navigation menu with links and job information
          </SheetDescription>
        </SheetHeader>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Search — same slot as the desktop sidebar: under the brand,
              above the nav links */}
          {onOpenSearch && (
            <div className="px-4 pt-4">
              <SearchTrigger
                onClick={() => {
                  onClose()
                  onOpenSearch()
                }}
                className="h-13"
              />
            </div>
          )}

          {/* Navigation Links */}
          <nav className="px-4 py-4">
            <div className="space-y-1">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  onClick={onClose}
                  className={({ isActive }) =>
                    cn(
                      'flex h-13 w-full items-center gap-3 rounded-full px-4 text-[15px] font-medium transition-colors',
                      isActive
                        ? 'bg-sidebar-primary text-sidebar-primary-foreground shadow-sm'
                        : 'hover:bg-[color-mix(in_srgb,currentColor_7%,transparent)]'
                    )
                  }
                >
                  {link.icon && (
                    <link.icon aria-hidden="true" className="h-[18px] w-[18px] shrink-0" />
                  )}
                  {link.label}
                </NavLink>
              ))}
            </div>
          </nav>

          {/* Job Context - if provided */}
          {jobContext && (
            <>
              <Separator className="my-4" />

              <div className="px-6 py-4">
                <div className="space-y-1">
                  <p className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
                    This job
                  </p>
                  <h2 className="font-semibold text-base">{jobContext.title}</h2>
                  <p className="text-sm text-muted-foreground">{jobContext.siteAddress}</p>
                </div>

                <Separator className="my-4" />

                {/* Job Action Buttons - only shown if any action is available */}
                {(jobContext.onEditJob || jobContext.onViewFiles || jobContext.onDownloadMaps) && (
                  <>
                    <div className="space-y-2">
                      {getJobDrawerActions(jobContext).map((action) =>
                        'to' in action ? (
                          <Button
                            asChild
                            key={action.key}
                            variant={action.variant}
                            className="w-full"
                          >
                            <Link to={action.to} onClick={onClose}>
                              {action.label}
                            </Link>
                          </Button>
                        ) : (
                          <Button
                            key={action.key}
                            type="button"
                            variant={action.variant}
                            className="w-full"
                            onClick={() => {
                              action.onClick()
                              onClose()
                            }}
                          >
                            {action.label}
                          </Button>
                        )
                      )}
                    </div>
                    <Separator className="my-4" />
                  </>
                )}

                {/* Job Information */}
                <div className="space-y-3 text-sm">
                  <h2 className="font-semibold text-sm mb-3">Job Information</h2>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Created</span>
                    <span className="font-medium">{jobContext.createdAt}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status</span>
                    <span className="font-medium capitalize">
                      {jobContext.status.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Trees</span>
                    <span className="font-medium">{jobContext.nodeCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Photos</span>
                    <span className="font-medium">{jobContext.photoCount}</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Sync status row — took the build stamp's slot (that now lives on
            Settings → About). Always reachable from a job page via the
            hamburger, which is the moment sync matters in the field. */}
        <button
          type="button"
          onClick={() => {
            onClose()
            onOpenSyncStatus()
          }}
          className="flex items-center gap-3 border-t px-6 py-4 text-left transition-colors hover:bg-[color-mix(in_srgb,currentColor_7%,transparent)]"
        >
          <syncMeta.icon
            aria-hidden="true"
            className={cn('h-4 w-4', syncMeta.colorClass, syncMeta.spin && 'animate-spin')}
          />
          <span className="flex-1 text-sm font-medium">Sync Status</span>
          {/* The icon already says "synced"/"syncing"/"offline"/"failed" —
              repeating it in words is noise. A pending count is the one state
              carrying a number no icon can express, so that label stays. */}
          {syncMeta.count !== undefined ? (
            <span className={cn('text-xs', syncMeta.colorClass)}>{syncMeta.text}</span>
          ) : (
            /* Colour and glyph are invisible to a screen reader, so the state
               still has to reach it — just not on screen */
            <span className="sr-only">, {syncMeta.text}</span>
          )}
        </button>
      </SheetContent>
    </Sheet>
  )
}
